import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Flex,
  Image,
  Text,
  Badge,
  Input,
  Button,
  Divider,
  useToast,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import {
  fetchAllPackages,
  fetchAllVehiclesList,
} from "../../actions/management.action";
import { Loading } from "../../components/Loading";
import { normalizeTags } from "../../utils/normalizeTags";

export default function PackageList({
  fromLocation,
  toLocation,
  pickupDate,
  pickupTime,
  returnDate,
  tripType = "OW",
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tagFilter, setTagFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const getTodayDate = () => {
    if (pickupDate) return pickupDate;
    return new Date().toLocaleDateString("en-GB");
  };

  const getTimeHHMM = () => {
    if (pickupTime) return pickupTime;
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const resolveVehicleSegmentIdToName = async (vehicleSegmentId) => {
    if (!vehicleSegmentId) return "";
    try {
      const res = await fetchAllVehiclesList();
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
      const match = list.find(
        (seg) =>
          String(seg?._id || seg?.id || "").toLowerCase() ===
          String(vehicleSegmentId).toLowerCase(),
      );
      return match?.segmentName || match?.name || "";
    } catch (e) {
      return "";
    }
  };

  const handleBookWithOption = async (pkg, amountType) => {
    const bookingTripType = tripType || "OW";
    // pkg.vehicleSegment contains the vehicle segment ID
    const vehicleSegmentId = pkg?.vehicleSegment;
    // Use vehicleSegmentName from package or resolve from ID
    let vehicleSegmentName = pkg?.vehicleSegmentName || "";
    if (!vehicleSegmentName && vehicleSegmentId) {
      vehicleSegmentName =
        await resolveVehicleSegmentIdToName(vehicleSegmentId);
    }

    const amount =
      amountType === "withStay" ? pkg.amountWithStay : pkg.amountWithoutStay;

    let gst = 0;
    let gstOnCommission = 0;
    try {
      const token = localStorage.getItem("prima_session");
      if (token) {
        // Calculate distance
        const distanceResponse = await axios.post(
          `${import.meta.env.VITE_API_URL}/management/calculate-distance`,
          {
            from: pkg.startLocation,
            to: pkg.endLocation,
            tripType: bookingTripType,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const distance = distanceResponse?.data?.data?.distance || 0;

        // Fetch fare details including GST
        const fareResponse = await axios.post(
          `${import.meta.env.VITE_API_URL}/management/find`,
          {
            from: pkg.startLocation,
            to: pkg.endLocation,
            tripType: bookingTripType,
            pickupDate: getTodayDate(),
            pickupTime: getTimeHHMM(),
            distance: distance,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (fareResponse?.data?.data && Array.isArray(fareResponse.data.data)) {
          const selectedFare = fareResponse.data.data.find(
            (fare) => fare.id === vehicleSegmentId,
          );

          if (selectedFare) {
            gst = selectedFare.gst || selectedFare.GST || 0;
            gstOnCommission =
              selectedFare.gstOnCommission ||
              selectedFare.gst_on_commission ||
              0;
          }
        }
      }
    } catch (error) {
      console.error("Error fetching GST:", error);
    }

    const bookingData = {
      packageId: pkg._id,
      packageName: pkg.packageTitle || pkg.packageDetails,
      from: pkg.startLocation,
      to: pkg.endLocation,
      travellers: pkg.travellers,
      category: pkg.category,
      amountWithoutStay: pkg.amountWithoutStay,
      amountWithStay: pkg.amountWithStay,
      selectedAmount: amount,
      amountType: amountType,
      gst: gst,
      gstOnCommission: gstOnCommission,
      vehicleSegmentName: vehicleSegmentName,
      vehicleSegmentId: vehicleSegmentId,
      tripType: bookingTripType,
      pickupdate: getTodayDate(),
      pickuptime: getTimeHHMM(),
      returndate: returnDate || "",
      inclusionsAndExclusions: normalizeTags(pkg?.inclusionsAndExclusions),
      stops: [],
      distance: 0,
    };

    localStorage.setItem("selectedPackageId", pkg._id);
    localStorage.setItem("lead_booking_data", JSON.stringify(bookingData));

    navigate("/bookings/lead/booking-details", {
      state: {
        packageId: pkg._id,
        packageName: pkg.packageTitle || pkg.packageDetails,
        start: pkg.startLocation,
        end: pkg.endLocation,
        travellers: pkg.travellers,
        category: pkg.category,
        image: pkg.image || pkg.Carimage,
        selectedAmount: amount,
        amountType: amountType,
        gst: gst,
        gstOnCommission: gstOnCommission,
        vehicleSegmentName: vehicleSegmentName,
      },
    });
  };

  const imageFallback = "https://placehold.co/160x100";
  const apiBase = import.meta.env.VITE_API_URL || "http://localhost:4500";
  const normalizeLocation = (loc) => {
    if (!loc) return "";
    if (typeof loc === "string") return loc;
    return loc?.name || loc?.description || loc?.address || "";
  };
  const getImageSrc = (pkg) => {
    const raw = pkg?.image || pkg?.Carimage || "";
    if (!raw) return imageFallback;
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    return `${apiBase}${raw.startsWith("/") ? "" : "/"}${raw}`;
  };

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);

        const allPackages = [];
        let page = 1;
        let totalPages = 1;

        while (page <= totalPages) {
          const res = await fetchAllPackages({ page, limit: 50 });
          const data = res?.data || [];
          allPackages.push(...data);

          const pagination = res?.pagination;
          totalPages = pagination?.totalPages || 1;
          page += 1;
        }

        setPackages(allPackages);
      } catch (err) {
        console.error("Error fetching packages:", err);
        toast({
          title: "Error fetching packages",
          description: err.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, [toast]);

  useEffect(() => {
    setCurrentPage(1);
  }, [fromLocation, toLocation, pickupDate, pickupTime, returnDate, tripType, tagFilter, packages.length]);

  if (loading) {
    return <Loading />;
  }

  const activePackages = packages.filter(
    (pkg) => String(pkg?.productStatus || "draft").toLowerCase() === "active",
  );

  const fromNeedle = normalizeLocation(fromLocation).toLowerCase().trim();
  const toNeedle = normalizeLocation(toLocation).toLowerCase().trim();
  const locationFilteredPackages = activePackages.filter((pkg) => {
    const pkgFrom = normalizeLocation(pkg?.startLocation).toLowerCase();
    const pkgTo = normalizeLocation(pkg?.endLocation).toLowerCase();
    const fromOk = fromNeedle ? pkgFrom.includes(fromNeedle) : true;
    const toOk = toNeedle ? pkgTo.includes(toNeedle) : true;
    return fromOk && toOk;
  });

  const filteredPackages = tagFilter
    ? locationFilteredPackages.filter((pkg) => {
      const needle = tagFilter.toLowerCase().trim();
      const tagsArray = normalizeTags(pkg?.tags).map((tag) =>
        String(tag).toLowerCase().trim(),
      );
      const searchableText = [
        pkg?.packageDetails,
        pkg?.packageTitle,
        pkg?.startLocation,
        pkg?.endLocation,
      ]
        .map((value) => normalizeLocation(value).toLowerCase().trim())
        .join(" ");

      return (
        tagsArray.some((tag) => tag.includes(needle)) ||
        searchableText.includes(needle)
      );
    })
    : locationFilteredPackages;

  const totalPages = Math.max(1, Math.ceil(filteredPackages.length / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * itemsPerPage;
  const paginatedPackages = filteredPackages.slice(startIndex, startIndex + itemsPerPage);

  if (!activePackages.length) {
    return (
      <Box p={4}>
        <Text>No active packages found.</Text>
      </Box>
    );
  }


  return (
    <>
      {/* ===== Package List UI ===== */}
      <Box
        p={6}
        bg="linear-gradient(135deg, #f5f7fa 0%, #fafbfc 100%)"
        borderRadius="lg"
        mb={4}
      >
        <Box maxW={{ base: "100%", md: "640px" }}>
          <Flex align="center" justify="space-between" mb={3}>

            <Text
              fontSize="sm"
              fontWeight="600"
              color="gray.700"
              textTransform="uppercase"
              letterSpacing="0.5px"
            >
              Filter Packages
            </Text>
            {tagFilter && (
              <Button
                size="xs"
                variant="ghost"
                colorScheme="gray"
                onClick={() => setTagFilter("")}
                fontSize="xs"
                px={2}
              >
                Clear
              </Button>
            )}
          </Flex>
          <InputGroup size="md">
            <InputLeftElement
              pointerEvents="none"
              children={<SearchIcon color="gray.400" />}
            />
            <Input
              placeholder="Search by tag, title, start or end location..."
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              bg="white"
              borderColor="gray.200"
              _hover={{ borderColor: "gray.300" }}
              _focus={{
                borderColor: "blue.400",
                boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.5)",
              }}
              pl={10}
              borderRadius="md"
              fontSize="sm"
              transition="all 0.2s"
            />
          </InputGroup>
          {tagFilter && (
            <Text fontSize="xs" color="gray.500" mt={2}>
              Showing packages matching "{tagFilter}"
            </Text>
          )}
        </Box>
      </Box>
      {filteredPackages.length === 0 && (
        <Box p={4}>
          <Text>No packages match the selected filters.</Text>
        </Box>
      )}
      {filteredPackages.length > 0 && (
        <>
          <Flex
            wrap="wrap"
            gap={6}
            p={4}
            alignContent="flex-start"
          >
            {paginatedPackages.map((pkg) => (
              <Box
                key={pkg._id}
                bg="white"
                borderRadius="2xl"
                shadow="md"
                overflow="hidden"
                flexBasis={{ base: "100%", md: "calc(33.333% - 16px)" }}
                p={6}
              >
                {/* Package Image */}
                <Image
                  src={getImageSrc(pkg)}
                  alt={pkg.packageDetails}
                  objectFit="cover"
                  w="100%"
                  h="160px"
                  borderRadius="lg"
                  mb={4}
                  onError={(e) => {
                    e.currentTarget.src = imageFallback;
                  }}
                />

                {/* Package Title */}
                <Text fontSize="xl" fontWeight="700" mb={3}>
                  {pkg.packageDetails}
                </Text>

                {/* Package Details */}
                <Box
                  bg="gray.50"
                  p={4}
                  borderRadius="lg"
                  mb={4}
                  fontSize="sm"
                  lineHeight="2"
                >
                  <Text>
                    • <strong>Start:</strong> {pkg.startLocation}
                  </Text>
                  <Text>
                    • <strong>End:</strong> {pkg.endLocation}
                  </Text>
                  <Text>
                    • <strong>Travellers:</strong> {pkg.travellers}
                  </Text>
                  <Text>
                    • <strong>Category:</strong> {pkg.category}
                  </Text>
                  {pkg.duration && (
                    <Text>
                      • <strong>Duration:</strong> {pkg.duration}
                    </Text>
                  )}
                </Box>

                {/* Badges */}
                <Flex gap={2} flexWrap="wrap" mb={4}>
                  {pkg.category && (
                    <Badge colorScheme="blue">{pkg.category}</Badge>
                  )}
                  {(() => {
                    const tagsArray = normalizeTags(pkg?.tags);
                    if (!tagsArray.length) return null;
                    return tagsArray.map((tag) => (
                      <Badge key={`${pkg._id}-${tag}`} colorScheme="purple">
                        {tag}
                      </Badge>
                    ));
                  })()}
                </Flex>

                <Divider my={4} />

                {/* Booking Options */}
                <Text fontSize="sm" fontWeight="600" mb={3}>
                  Select Booking Option
                </Text>

                <Flex gap={3} flexDirection="column">
                  {/* Without Stay */}
                  <Button
                    width="100%"
                    colorScheme="blue"
                    variant="outline"
                    borderRadius="lg"
                    fontSize="sm"
                    p={3}
                    h="auto"
                    onClick={() => handleBookWithOption(pkg, "withoutStay")}
                    _hover={{ bg: "blue.50" }}
                  >
                    <Flex width="100%" justify="space-between" align="center">
                      <Box textAlign="left">
                        <Text fontWeight="600">Without Stay</Text>
                        <Text fontSize="xs" color="gray.600">
                          Accommodation not included
                        </Text>
                      </Box>
                      <Text fontWeight="700" color="blue.600" fontSize="md">
                        ₹{pkg.amountWithoutStay?.toLocaleString() || "0"}
                      </Text>
                    </Flex>
                  </Button>

                  {/* With Stay */}
                  <Button
                    width="100%"
                    colorScheme="green"
                    variant="outline"
                    borderRadius="lg"
                    fontSize="sm"
                    p={3}
                    h="auto"
                    onClick={() => handleBookWithOption(pkg, "withStay")}
                    _hover={{ bg: "green.50" }}
                  >
                    <Flex width="100%" justify="space-between" align="center">
                      <Box textAlign="left">
                        <Text fontWeight="600">With Stay</Text>
                        <Text fontSize="xs" color="gray.600">
                          Accommodation included
                        </Text>
                      </Box>
                      <Text fontWeight="700" color="green.600" fontSize="md">
                        ₹{pkg.amountWithStay?.toLocaleString() || "0"}
                      </Text>
                    </Flex>
                  </Button>
                </Flex>
              </Box>
            ))}
          </Flex>

          <Flex justify="space-between" align="center" px={4} pb={4} gap={3} wrap="wrap">
            <Text fontSize="sm" color="gray.600">
              Showing {filteredPackages.length === 0 ? 0 : startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredPackages.length)} of {filteredPackages.length}
            </Text>

            <Flex gap={2} align="center">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage(Math.max(1, safeCurrentPage - 1))}
                isDisabled={safeCurrentPage <= 1}
              >
                Previous
              </Button>

              <Text fontSize="sm" fontWeight="600">
                Page {safeCurrentPage} of {totalPages}
              </Text>

              <Button
                size="sm"
                variant="outline"
                onClick={() => setCurrentPage(Math.min(totalPages, safeCurrentPage + 1))}
                isDisabled={safeCurrentPage >= totalPages}
              >
                Next
              </Button>
            </Flex>
          </Flex>
        </>
      )}
    </>
  );
}
